import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Skeleton } from 'antd';
import moment from 'moment';
import WarningWithAction from '$src/components/WarningWithAction';
import { vsgModeStatus } from '$src/api/constants';
import { redirectToWatchZones, redirectToURL } from '$src/utils/redirect';
import AuthorizeDialog from '$src/components/AuthorizeDialog';
import GeofenceDialog from '$src/components/GeofenceDialog';
import useModalEvents from '$src/hooks/useModalEvents';
import GuardMonitoring from '$src/components/GuardMonitoring';

import NavigationTitle from '$src/components/NavigationTitle';
import useStores from '$src/hooks/useStores';
import useFeature from '$src/hooks/useFeature';
import { OVERAGES_EDUCATION_ARTICLE_URL, BILLING_PLANS_URL } from '$src/constants/urls';
import useLocationContext from '$src/hooks/useLocationContext';
import GraphOveragesCell from '$src/components/GraphOveragesCell/GraphOveragesCell';
import { Cell } from '@amzn/ring-react-components/csr/components/Cell';
import { Link } from '@amzn/ring-react-components/csr/components/Link';
import { CalloutCard } from '@amzn/ring-react-components/csr/components/CalloutCard';
import { HomeIcon, EventIcon, SecureProtectionIcon, WarningIcon, GeofencingIcon } from '@ring-icons';
import EventCard from '$src/components/EventCard';
import MonitoringZonesModal from '$src/components/MonitoringZonesDialog';
import eventsToIconsMap, { guardReviewed } from './EventsToIconsMap';

import './EventSummary.less';

function EventSummary() {
  const { eventStore, sessionStore, locationContextStore, modesStore } = useStores();
  const { fetchEventSummary, eventPercentage, loading, guardReviewedEvents, getResolvedEvent, resolvedEvents } = eventStore;
  const locationId = useLocationContext();
  const { locationOwnerId } = locationContextStore;
  const { authorizeVSG, isMobile } = sessionStore;
  const { vsgErrorState, vsgMode } = modesStore;
  const { location } = locationContextStore;
  const [toggleModal, showModal] = useModalEvents();
  const [toggleGeofenceDialog, showGeofenceDialog] = useModalEvents();
  const [toggleMonitoringZonesModal, showMonitoringZonesModal] = useModalEvents();
  const startDate = moment.unix(location?.overagesStartAt);
  const overagesThreshold = location?.overagesThreshold ?? 0;
  const overagesEnabled = location?.overagesEnabled;
  const overCapacity = resolvedEvents >= overagesThreshold;
  const overagesCap = location?.overagesCap;
  const [isEventAndOveragesFeatureEnabled] = useFeature('stark-feature-next-event-overages');
  const [isLocationPausedFeatureEnabled] = useFeature('stark-feature-next-location-paused');
  const [isGeofenceSnoozeFeatureEnabled] = useFeature('stark-feature-next-geofence-snooze');
  const [isMonitoringZonesEnabled] = useFeature('stark-feature-next-video-monitoring-zones');

  useEffect(() => {
    if (locationOwnerId && locationId) {
      fetchEventSummary(locationOwnerId, locationId);
    }
  }, [fetchEventSummary, locationOwnerId, locationId]);

  const cards = Object.keys(eventsToIconsMap);

  const shouldRenderVSGUnauthorizedCard = vsgErrorState === vsgModeStatus.AUTH_NOT_FOUND;
  const shouldRenderWarning = !isMobile && vsgMode;
  const isEventAndOveragesVisible = !shouldRenderWarning && isEventAndOveragesFeatureEnabled && overCapacity;
  const isGuardMonitoringEnabled = !vsgModeStatus.EVENTS_EXCEEDED.includes(vsgErrorState);
  const shouldRenderMonitoringZonesWarning = isMonitoringZonesEnabled && vsgErrorState === vsgModeStatus.MISSING_MONITORING_ZONE;
  const shouldRenderGeofenceInfo =
    !shouldRenderWarning &&
    !isEventAndOveragesVisible &&
    isGeofenceSnoozeFeatureEnabled &&
    (getResolvedEvent('AlarmResolved') >= 3 || getResolvedEvent('Passerby') >= 10);

  const handleMonitoringZonesClick = () => {
    const didRedirect = redirectToWatchZones(locationId);
    if (!didRedirect) {
      toggleMonitoringZonesModal();
    }
  };

  const renderWarning = () => {
    if (shouldRenderVSGUnauthorizedCard) {
      return <WarningWithAction {...vsgMode} onClick={toggleModal} />;
    }
    if (shouldRenderMonitoringZonesWarning) {
      return <WarningWithAction {...vsgMode} onClick={handleMonitoringZonesClick} />;
    }
    return <WarningWithAction {...vsgMode} />;
  };

  const renderGeofenceInfo = () => {
    const geofenceDialogClosed = JSON.parse(sessionStorage.getItem('geofence-dialog-closed')) === true;

    if (geofenceDialogClosed) {
      return null;
    }

    return (
      <CalloutCard
        icon={<GeofencingIcon width="22px" />}
        status="default"
        title="Virtual Security Guard works with Geofence."
        data-testid="geofence-warning"
      >
        <p>Automate how the service works with your Ring devices by setting up your Geofence.</p>
        <Link color="primary" onClick={toggleGeofenceDialog} type="external" data-testid="geofence-warning-learn-more">
          Learn More
        </Link>
      </CalloutCard>
    );
  };

  const renderCalloutCard = () => {
    if (overagesEnabled || !overCapacity) return null;

    return (
      <CalloutCard
        className="event-summary__callout-card"
        data-testid="warning-maximum-events"
        icon={<WarningIcon width="22px" fill="red" />}
        status="error"
        title="Virtual Security Guard Event Limit Reached"
      >
        Guard monitoring is off because you've reached your limit of {overagesThreshold} monitored events. Opt in to&nbsp;
        <Link size={isMobile ? 'xs' : 's'} color="primary" link={OVERAGES_EDUCATION_ARTICLE_URL} type="external">
          Extra Events
        </Link>
        &nbsp;to continue monitoring, or your service will resume on your next billing cycle.
      </CalloutCard>
    );
  };

  const extraEventsDescription = () => {
    let description;

    if (overagesEnabled) {
      if (overCapacity) {
        if (overagesCap) {
          description = `Guards will monitor up to ${overagesCap} Extra Events in this billing cycle for $0.99 per event.`;
        } else {
          description = (
            <>
              <span>
                Guards will continue monitoring motion events for $0.99/event until the end of your billing cycle. At any time, you can set an Extra
                Events limit or turn off Extra Events by clicking{' '}
              </span>
              <Link
                color="primary"
                onClick={() => redirectToURL(BILLING_PLANS_URL)}
                type="external"
                size={isMobile ? 'xs' : 's'}
                data-testid="extra-events-set-overages-limit"
              >
                here
              </Link>
              <span>.</span>
            </>
          );
        }
      } else {
        description = `Guards will continue monitoring motion events at $0.99 per event once you’ve used all ${overagesThreshold} included events each month.`;
      }
    } else {
      description = `Opt in to Extra Events to keep Virtual Security Guard active after you've used all ${overagesThreshold} included events each month.`;
    }

    return description;
  };

  return (
    <div className="page-devices-wrapper event-summary" data-testid="event-summary-page">
      <NavigationTitle title="Virtual Security Guard" icon={HomeIcon}>
        {!shouldRenderWarning && isEventAndOveragesFeatureEnabled && renderCalloutCard()}
        {shouldRenderGeofenceInfo && renderGeofenceInfo()}
      </NavigationTitle>
      {shouldRenderWarning && renderWarning()}
      <Skeleton active loading={loading}>
        {isLocationPausedFeatureEnabled && <GuardMonitoring enabled={isGuardMonitoringEnabled} />}
        {isEventAndOveragesFeatureEnabled && (
          <>
            <div className="event-summary__content-title">
              <p>Event Count</p>
              <span> Since {startDate.format('DD-MMMM')} </span>
            </div>
            <GraphOveragesCell
              eventCount={resolvedEvents}
              overagesThreshold={overagesThreshold}
              eventPercentage={eventPercentage}
              overagesEnabled={overagesEnabled}
              overagesCap={overagesCap}
            />
            <Cell
              data-testid="extra-events"
              image={<EventIcon />}
              title={`Extra Events: ${overagesEnabled ? 'On' : 'Off'}`}
              rightBlock={
                <Link color="primary" link={OVERAGES_EDUCATION_ARTICLE_URL} type="external">
                  Manage
                </Link>
              }
            >
              {extraEventsDescription()}
            </Cell>
          </>
        )}
        <>
          {!isEventAndOveragesFeatureEnabled && (
            <>
              <p>Event Summary</p>
              <Cell image={<SecureProtectionIcon />} title={`${resolvedEvents} events`}>
                Since {startDate.format('MM/DD/YYYY')}
              </Cell>
            </>
          )}
          <p>Guard Event Summary</p>
          <div className="event-summary__cards-container">
            {cards.map((e) => (
              <EventCard
                Icon={eventsToIconsMap[e].icon}
                isRedIcon={!!eventsToIconsMap[e].isEmergency}
                numberOfEvents={getResolvedEvent(e)}
                cardName={eventsToIconsMap[e].displayName}
                cardDescription={eventsToIconsMap[e].description}
                key={e}
              />
            ))}
            <EventCard
              Icon={guardReviewed.icon}
              numberOfEvents={guardReviewedEvents}
              cardName={guardReviewed.displayName}
              cardDescription={guardReviewed.description}
              key="guard-reviewed-card"
            />
          </div>
        </>
      </Skeleton>
      <GeofenceDialog showModal={showGeofenceDialog} toggleGeofenceDialog={toggleGeofenceDialog} />
      <AuthorizeDialog showModal={showModal} handleAuthorize={() => authorizeVSG()} setModalVisible={toggleModal} />
      <MonitoringZonesModal showModal={showMonitoringZonesModal} handleClick={toggleMonitoringZonesModal} locationId={locationId} />
    </div>
  );
}

export default observer(EventSummary);
